import React, { useEffect, useState } from 'react';
import { Icon } from './inboxIcons';
import { reportProblem } from './ProblemReport';

const STAGES = ['Placed', 'Printing', 'Packed', 'Shipped', 'Delivered'];
const REASON = {
  pinned: 'Pinned',
  mentioned: 'Mentioned in message',
  only_open: 'Only open order',
  latest: 'Most recent',
  chosen: 'Chosen from all orders',
};
const PILL = {
  queued: 'cx-c-plain', printing: 'cx-c-ai', printed: 'cx-c-resolved', packed: 'cx-c-resolved',
  shipped: 'cx-c-open', delivered: 'cx-c-resolved', cancelled: 'cx-c-new', rto: 'cx-c-new',
};

function Photo({ item, size }) {
  const [broken, setBroken] = useState(false);
  if (!item.image || broken) return <span className="cx-ord-photo none" style={{ width: size, height: size }} aria-hidden="true"><Icon name="shop" size={size > 40 ? 18 : 14} /></span>;
  return <img className="cx-ord-photo" src={item.image} alt="" width={size} height={size} loading="lazy" onError={() => setBroken(true)} />;
}

// "Order in question" card: pinned/mentioned order first, per-fabric status with photos, switch and pin
// Failed delivery: ask Delhivery to come again (tomorrow or a date) or send a corrected address
function NdrBox({ ndr, api, root, number, toast, onDone }) {
  const [mode, setMode] = useState(null);
  const [date, setDate] = useState('');
  const [f, setF] = useState({ address: '', pincode: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const send = async (body) => {
    setBusy(true);
    try {
      const r = await api(`${root}/order/${number}/ndr`, 'POST', body);
      toast?.(r.message);
      setMode(null);
      onDone?.(r.order);
    } catch (e) {
      toast?.(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  const min = new Date(Date.now() + 864e5).toISOString().slice(0, 10);
  const max = new Date(Date.now() + 6 * 864e5).toISOString().slice(0, 10);
  return (
    <div className="cx-ndr">
      <div className="cx-ndr-head">
        <Icon name="alert" size={15} />
        <span>{ndr.scheduled && !ndr.active ? <>Reattempt booked{ndr.reattemptDate ? ` for ${ndr.reattemptDate}` : ''}</> : <>Delivery failed{ndr.reason ? `: ${ndr.reason}` : ''}</>}</span>
      </div>
      {ndr.canAct ? (
        mode === 'date' ? (
          <div className="cx-ndr-form">
            <div className="cx-field"><label htmlFor="ndr-date">Deliver on</label><input id="ndr-date" type="date" min={min} max={max} value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div className="cx-ndr-btns">
              <button className="cx-btn cx-btn-sm cx-btn-primary" disabled={busy || !date} onClick={() => send({ action: 'reattempt', date })}>Send to Delhivery</button>
              <button className="cx-linkbtn" onClick={() => setMode(null)}>Cancel</button>
            </div>
          </div>
        ) : mode === 'address' ? (
          <div className="cx-ndr-form">
            <div className="cx-field"><label htmlFor="ndr-add">New address</label><textarea id="ndr-add" rows={3} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
            <div className="cx-ndr-row">
              <div className="cx-field"><label htmlFor="ndr-pin">Pincode</label><input id="ndr-pin" inputMode="numeric" maxLength={6} value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value.replace(/\D/g, '') })} /></div>
              <div className="cx-field"><label htmlFor="ndr-ph">Phone (optional)</label><input id="ndr-ph" inputMode="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
            </div>
            <p className="cx-ndr-note">Delhivery delivers to the new address on the next attempt. Pincode change may not be allowed by Delhivery.</p>
            <div className="cx-ndr-btns">
              <button className="cx-btn cx-btn-sm cx-btn-primary" disabled={busy || f.address.trim().length < 10} onClick={() => send({ action: 'address', ...f })}>Send to Delhivery</button>
              <button className="cx-linkbtn" onClick={() => setMode(null)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="cx-ndr-btns">
            <button className="cx-btn cx-btn-sm cx-btn-primary" disabled={busy} onClick={() => send({ action: 'reattempt' })}>Reattempt tomorrow</button>
            <button className="cx-btn cx-btn-sm" disabled={busy} onClick={() => setMode('date')}>Pick a date</button>
            <button className="cx-btn cx-btn-sm" disabled={busy} onClick={() => setMode('address')}>Update address</button>
          </div>
        )
      ) : <p className="cx-ndr-note">Not a Delhivery shipment. Ask the courier for a reattempt.</p>}
    </div>
  );
}

// Ask the office (Order Desk) about this order; their answer comes back here as a team note
function AskProduction({ api, root, number, toast }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api(`${root}/order/${number}/ask`, 'POST', { question: text });
      toast?.('Asked Order Desk. The answer will appear in this conversation.');
      setText(''); setOpen(false);
    } catch (e) { toast?.(e.message, true); } finally { setBusy(false); }
  };
  if (!open) return <button className="cx-linkbtn" onClick={() => setOpen(true)}>Ask production</button>;
  return (
    <div className="cx-ndr-form" style={{ padding: '0 12px 12px' }}>
      <div className="cx-field"><label htmlFor={`ask-${number}`}>Question for the office / production team</label><textarea id={`ask-${number}`} rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. When will this order be dispatched?" /></div>
      <div className="cx-ndr-btns"><button className="cx-btn cx-btn-sm cx-btn-primary" disabled={busy || text.trim().length < 3} onClick={send}>Send to Order Desk</button><button className="cx-linkbtn" onClick={() => setOpen(false)}>Cancel</button></div>
    </div>
  );
}

// base: where to fetch other orders (a cart uses its own read-only route); readOnly: no pin / verify
function InboxOrder({ api, ticketId, card, onPinned, onVerified, toast, bare = false, base = null, readOnly = false }) {
  const root = base || `/api/v1/support/inbox/${ticketId}`;
  const [sel, setSel] = useState(card.selected);
  const [chosen, setChosen] = useState(false);
  const [details, setDetails] = useState(card.details || {});
  const [open, setOpen] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);

  // New conversation: start fresh. Same conversation (pin, verify, refresh): keep what's open, merge new data.
  useEffect(() => {
    setSel(card.selected);
    setDetails(card.details || {});
    setChosen(false);
    setShowAll(false);
    setOpen(null);
  }, [ticketId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setDetails((p) => ({ ...p, ...(card.details || {}) }));
    if (card.pinned) { setSel(card.pinned); setChosen(false); }
  }, [card]);

  const d = sel ? details[sel] : null;
  const expanded = open ?? !!(d?.items && d.items.length <= 3);

  const pick = async (n) => {
    setSel(n);
    setChosen(n !== card.selected);
    setShowAll(false);
    setOpen(null);
    if (!details[n]) {
      try {
        const r = await api(`${root}/order/${n}`);
        setDetails((p) => ({ ...p, [n]: r.order }));
      } catch (e) {
        toast?.(e.message, true);
      }
    }
  };

  const pin = async () => {
    setBusy(true);
    const isPinned = card.pinned === sel;
    try {
      await api(`/api/v1/support/inbox/${ticketId}/pin-order`, 'POST', { number: isPinned ? null : sel });
      toast?.(isPinned ? 'Unpinned' : `Order #${sel} pinned. The AI agent will use it.`);
      onPinned?.(isPinned ? null : sel, details[sel]);
    } catch (e) {
      toast?.(e.message, true);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api(`/api/v1/support/inbox/${ticketId}/verify-order`, 'POST', { number: sel });
      const r = await api(`/api/v1/support/inbox/${ticketId}/order/${sel}`);
      setDetails((p) => ({ ...p, [sel]: r.order }));
      onVerified?.(sel, r.order);
      toast?.('Marked as verified');
    } catch (e) {
      toast?.(e.message, true);
    } finally {
      setBusy(false);
    }
  };

  if (!sel) {
    if (!card.all.length) return <p className="cx-summary">No orders found for this customer. For chats, ask for the order number and the phone used.</p>;
    // Wholesale, product or general question: no order to show until one is mentioned or picked
    return (
      <div className="cx-ord-wrap">
        <div className="cx-ord-none">
          <span>No order mentioned in this conversation.</span>
          <button className="cx-linkbtn" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>{showAll ? 'Hide orders' : `${card.all.length} past order${card.all.length === 1 ? '' : 's'}`}</button>
        </div>
        {showAll && (
          <div className="cx-ord-all">
            {card.all.map((o) => (
              <button key={o.number} onClick={() => pick(o.number)}>
                <b className="cx-mono">#{o.number}</b>
                <span>{o.placedOn} · {o.total}</span>
                <span className="cx-chip cx-c-plain">{o.stageText}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const isPinned = card.pinned === sel;
  const reason = isPinned ? (card.pinnedByName ? `Pinned by ${card.pinnedByName.split(/[\s@]/)[0]}` : REASON.pinned) : chosen ? REASON.chosen : REASON[card.reason] || '';
  const tabs = [...new Set([...card.tabs, ...(chosen ? [sel] : [])])];
  const photos = (d?.items || []).filter((i) => i.status !== 'cancelled');

  return (
    <div className="cx-ord-wrap">
      {bare
        ? reason && <div><span className={`cx-ord-reason${isPinned ? ' pinned' : ''}`}>{reason}</span></div>
        : <h3>Order in question{reason && <span className={`cx-ord-reason${isPinned ? ' pinned' : ''}`}>{reason}</span>}</h3>}
      {tabs.length > 1 && (
        <div className="cx-ord-tabs" role="tablist" aria-label="Orders in this conversation">
          {tabs.map((n) => {
            const on = details[n]?.placedOn || card.all.find((o) => o.number === n)?.placedOn;
            return <button key={n} role="tab" aria-selected={n === sel} onClick={() => pick(n)}>#{n}{on && <span className="cx-ord-tabdate"> · {on}</span>}</button>;
          })}
        </div>
      )}

      <div className="cx-ord">
        {!d ? <div className="cx-skel" style={{ height: 90, margin: 12 }} /> : d.missing ? (
          <div className="cx-ord-body"><b className="cx-mono">#{d.number}</b><p className="cx-summary">No order with this number. Check the number with the customer.</p></div>
        ) : d.verified === false ? (
          <div className="cx-ord-body" style={{ display: 'grid', gap: 10 }}>
            <div className="cx-ord-top"><b className="cx-mono">#{d.number}</b><span className="cx-chip cx-c-waiting">Not verified</span></div>
            <div className="cx-ord-warn"><Icon name="alert" size={16} /><span>This order is on another customer's email and phone. Ask them to confirm the phone number used for the order before sharing any details.</span></div>
            <p className="cx-summary">Items, address and tracking stay hidden until verified. The AI agent follows the same rule.</p>
            {!readOnly && <div><button className="cx-btn cx-btn-sm" onClick={verify} disabled={busy}>Verified, show order</button></div>}
          </div>
        ) : (
          <>
            {d.late && <div className="cx-ord-late"><Icon name="alert" size={15} /><span>{d.eta}</span></div>}
            {d.rto && <div className="cx-ord-late cx-ord-rto"><Icon name="back" size={15} /><span>{d.eta}</span></div>}
            {d.ndr && !readOnly && <NdrBox ndr={d.ndr} api={api} root={root} number={d.number} toast={toast} onDone={(o) => o && setDetails((p) => ({ ...p, [d.number]: o }))} />}
            <button className="cx-ord-head" onClick={() => setOpen(!expanded)} aria-expanded={expanded}>
              <span className="cx-ord-top"><b className="cx-mono">#{d.number}</b><span className="cx-ord-pay">{d.payment && <span className={`cx-chip ${d.payment.kind === 'cod' ? 'cx-c-waiting' : d.payment.kind === 'paid' ? 'cx-c-resolved' : 'cx-c-plain'}`}>{d.payment.label}</span>}<span className="cx-mono">{d.total}</span></span></span>
              {d.dateLine && <span className="cx-ord-date">{d.dateLine}</span>}
              <span className="cx-ord-sum">
                <span className="cx-ord-strip">
                  {photos.slice(0, 4).map((i, k) => <Photo key={`${i.sku}-${k}`} item={i} size={40} />)}
                  {photos.length > 4 && <span className="cx-ord-more">+{photos.length - 4}</span>}
                </span>
                <span className="cx-ord-text"><b>{d.summary}</b><span>{d.statusSummary}</span>{d.inStockCount > 0 && <span className="cx-ord-instock"><Icon name="check" size={13} />{d.inStockCount} waiting fabric{d.inStockCount === 1 ? ' is' : 's are'} in stock</span>}</span>
                <Icon name="down" size={18} className={expanded ? 'cx-rot180' : ''} />
              </span>
            </button>
            {!readOnly && (
              <button type="button" className="cx-linkbtn cx-ord-report" onClick={() => reportProblem({
                where: 'order', type: 'wrong_info', orderNumber: d.number, section: 'stage',
                snapshot: { stage: d.statusSummary || '', summary: d.summary || '', dates: d.dateLine || '', eta: d.eta || '', late: !!d.late, payment: d.payment?.label || '' },
              })}><Icon name="flag" size={13} />Order info wrong? Report it</button>
            )}
            {expanded && (
              <div className="cx-ord-items">
                {d.items.map((i, k) => (
                  <div key={`${i.sku}-${k}`} className={`cx-ord-item${i.status === 'cancelled' ? ' cancelled' : ''}`}>
                    <Photo item={i} size={48} />
                    <div>
                      <div className="cx-ord-name">{i.name}</div>
                      <div className="cx-ord-meta">
                        <span className="cx-mono">{i.metres} m{i.unitPrice ? ` · ₹${Math.round(i.unitPrice)}/m` : ''}</span>
                        <span className={`cx-chip ${PILL[i.status] || 'cx-c-plain'}`}>{i.statusLabel}</span>
                      </div>
                      {i.stock?.fits && (
                        <div className="cx-ord-stock ok"><Icon name="check" size={13} />In stock · piece <span className="cx-mono">{i.stock.pieceId}</span> · {i.stock.length} m{i.stock.location ? ` · ${i.stock.location}` : ''}{i.stock.count > 1 ? ` (+${i.stock.count - 1} more)` : ''}. Can ship now</div>
                      )}
                      {i.stock && !i.stock.fits && (
                        <div className="cx-ord-stock">Only {i.stock.longest} m in stock (needs {i.remaining} m in one piece)</div>
                      )}
                      {!i.stock && ['queued', 'printing'].includes(i.status) && <div className="cx-ord-stock">Not in stock</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="cx-ord-progress">
              {d.stage >= 0 ? (
                <div className="cx-stages">
                  {(d.rto ? [...STAGES.slice(0, 4), d.rtoLabel === 'Returned to us' ? 'Returned' : 'Returning'] : STAGES).map((s, i) => (
                    <div key={s} className={`cx-stage ${i < d.stage ? 'done' : i === d.stage ? (d.rto ? 'now rto' : 'now') : ''}`}>
                      <i />{s}<span className="cx-num">{d.stepDates?.[i] || ''}</span>
                    </div>
                  ))}
                </div>
              ) : <span className="cx-chip cx-c-new">Cancelled</span>}
              {!d.late && (
                <div className="cx-order-eta"><Icon name="truck" /><span>{d.eta}</span>{d.trackingUrl && <a href={d.trackingUrl} target="_blank" rel="noreferrer">Track</a>}</div>
              )}
              {d.late && d.trackingUrl && <div className="cx-order-eta"><Icon name="truck" /><span>Tracking available</span><a href={d.trackingUrl} target="_blank" rel="noreferrer">Track</a></div>}
            </div>
          </>
        )}
        <div className="cx-ord-foot">
          {!readOnly && d && !d.missing && d.verified !== false && (
            <button className={`cx-btn cx-btn-sm${isPinned ? ' cx-pinned' : ''}`} onClick={pin} disabled={busy}>
              <Icon name="pin" size={14} />{isPinned ? 'Pinned · unpin' : 'Pin to this conversation'}
            </button>
          )}
          {!readOnly && d && !d.missing && d.verified !== false && !d.cancelled && <AskProduction api={api} root={root} number={d.number} toast={toast} />}
          <span className="cx-spacer" />
          {card.all.length > 0 && <button className="cx-linkbtn" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>{showAll ? 'Hide orders' : `All orders (${card.all.length})`}</button>}
        </div>
      </div>

      {showAll && (
        <div className="cx-ord-all">
          {card.all.map((o) => (
            <button key={o.number} onClick={() => pick(o.number)} aria-current={o.number === sel}>
              <b className="cx-mono">#{o.number}</b>
              <span>{o.placedOn} · {o.total}{o.cod ? ' · COD' : ''}{card.pinned === o.number ? ' · pinned' : ''}</span>
              <span className="cx-chip cx-c-plain">{o.stageText}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default InboxOrder;
